import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  Select,
  Option,
  FormLabel,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Chip,
  Tooltip,
  CircularProgress,
  Breadcrumbs,
} from '@mui/joy';
import {
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  KeyboardArrowRight,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import Sheet from '@mui/joy/Sheet';
import { DataGrid } from '@mui/x-data-grid';
import {
  getUserRoles,
  createUserRole,
  updateUserRole,
  deleteUserRole,
  getRolePermissions,
} from '../../main/API/authentication';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import DeleteAlert from '../../main/widgets/DeleteAlert';
import CustomerOptionList from '../../main/widgets/CustomersOptionList';
import RoleOptionList from '../../main/widgets/RoleOptionList';
import { getPaginatedOrKeyedResults } from '../../main/utils/pagination';
import {
  buildNavigationTree,
  autoActionKey,
  humanize,
  permissionCodenameLabelKey,
} from './widgets/RolePermissionsByPageEditor';

// ---------------------------------------------------------------------------
// RolePreviewPanel
// Shown inside the assign/edit modal once a role is selected. Using the same
// navigation tree that powers RolePermissionsByPageEditor, it groups the
// role's permissions by menu group → page → action so the admin sees exactly
// which pages and affordances the role unlocks *before* saving.
// ---------------------------------------------------------------------------
function RolePreviewPanel({ loading, error, permissions, t }) {
  const tree = useMemo(() => buildNavigationTree(), []);

  const permByCode = useMemo(() => {
    const m = new Map();
    (permissions || []).forEach((p) => {
      const code = p.codename || p.name;
      if (code) m.set(code, p);
    });
    return m;
  }, [permissions]);

  const allKnownCodes = useMemo(() => {
    const s = new Set();
    tree.forEach((g) => g.pages.forEach((pg) => pg.permissions.forEach((p) => s.add(p.code))));
    return s;
  }, [tree]);

  const groupedUsage = useMemo(() => {
    const out = [];
    tree.forEach((group) => {
      const pages = [];
      group.pages.forEach((page) => {
        const matches = page.permissions.filter((p) => permByCode.has(p.code));
        if (matches.length === 0) return;
        pages.push({
          id: page.id,
          labelKey: page.labelKey,
          route: page.route,
          actions: matches.map((p) => ({
            code: p.code,
            perm: permByCode.get(p.code),
            actionKey: p.actionKey || p.roleKey || autoActionKey(p.code),
            isRoute: p.roleKey === 'settingsPages.rolesPermsEditor.routeAccess',
          })),
        });
      });
      if (pages.length > 0) {
        out.push({ id: group.id, labelKey: group.labelKey, pages });
      }
    });
    return out;
  }, [tree, permByCode]);

  const orphans = useMemo(
    () =>
      (permissions || []).filter((p) => {
        const code = p.codename || p.name;
        return code && !allKnownCodes.has(code);
      }),
    [permissions, allKnownCodes]
  );

  const permCount = (permissions || []).length;
  const pageCount = groupedUsage.reduce((n, g) => n + g.pages.length, 0);

  if (loading) {
    return (
      <Sheet variant="soft" color="neutral" sx={{ p: 1.5, borderRadius: 'md', textAlign: 'center' }}>
        <CircularProgress size="sm" />
        <Typography level="body-sm" sx={{ mt: 0.5 }}>
          {t('settingsPages.userRoles.rolePreviewLoading')}
        </Typography>
      </Sheet>
    );
  }
  if (error) {
    return (
      <Sheet variant="soft" color="danger" sx={{ p: 1.5, borderRadius: 'md' }}>
        <Typography level="body-sm">
          {t('settingsPages.userRoles.rolePreviewLoadFailed')}
        </Typography>
      </Sheet>
    );
  }
  if (permCount === 0) {
    return (
      <Sheet variant="soft" color="neutral" sx={{ p: 1.5, borderRadius: 'md' }}>
        <Typography level="body-sm" startDecorator={<InfoOutlinedIcon fontSize="small" />}>
          {t('settingsPages.userRoles.rolePreviewEmpty')}
        </Typography>
      </Sheet>
    );
  }

  return (
    <Sheet
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 'md',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'min(320px, 40vh)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1, flexShrink: 0 }}>
        <Typography level="title-sm" startDecorator={<InfoOutlinedIcon fontSize="small" />}>
          {t('settingsPages.userRoles.rolePreviewTitle')}
        </Typography>
        <Chip size="sm" variant="soft" color="primary">
          {t('settingsPages.userRoles.rolePreviewSummary', { permCount, pageCount })}
        </Chip>
      </Box>
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          minHeight: 0,
          pr: 0.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
      <Stack spacing={1}>
        {groupedUsage.map((g) => (
          <Sheet
            key={g.id}
            variant="soft"
            color="neutral"
            sx={{ p: 1, borderRadius: 'sm' }}
          >
            <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t(g.labelKey)}
            </Typography>
            <Stack spacing={0.75}>
              {g.pages.map((pg) => (
                <Box key={pg.id} sx={{ pl: 1 }}>
                  <Typography level="body-sm">
                    {t(pg.labelKey)}
                    {pg.route ? (
                      <Typography
                        level="body-xs"
                        sx={{
                          ml: 0.5,
                          color: 'text.tertiary',
                          fontFamily:
                            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                        }}
                      >
                        {pg.route}
                      </Typography>
                    ) : null}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                    {pg.actions.map((a) => {
                      const permName = t(permissionCodenameLabelKey(a.code), {
                        defaultValue: (a.perm && a.perm.name) || humanize(a.code),
                      });
                      return (
                      <Tooltip
                        key={`${pg.id}-${a.code}`}
                        title={permName}
                        placement="top"
                      >
                        <Chip
                          size="sm"
                          variant={a.isRoute ? 'soft' : 'outlined'}
                          color={a.isRoute ? 'primary' : 'neutral'}
                          sx={{ fontSize: 10 }}
                        >
                          {a.actionKey
                            ? t(a.actionKey)
                            : permName}
                        </Chip>
                      </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Sheet>
        ))}
        {orphans.length > 0 && (
          <Sheet variant="soft" color="warning" sx={{ p: 1, borderRadius: 'sm' }}>
            <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('settingsPages.rolesPermsEditor.otherPermissions')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {orphans.map((p) => {
                const c = p.codename || p.name;
                return (
                <Chip
                  key={p.id || c}
                  size="sm"
                  variant="outlined"
                  color="warning"
                  sx={{ fontSize: 10 }}
                >
                  {t(permissionCodenameLabelKey(c), { defaultValue: p.name || c })}
                </Chip>
                );
              })}
            </Box>
          </Sheet>
        )}
      </Stack>
      </Box>
    </Sheet>
  );
}

export default function UserRolesScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    search: '',
    user: '',
    role: '',
    is_active: '',
  });
  const [openModal, setOpenModal] = useState(false);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [formData, setFormData] = useState({
    user: null,
    role: '',
    expires_at: '',
    is_active: true,
    branch: null,
  });
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  
  // Delete alert states
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedUserRoleId, setSelectedUserRoleId] = useState(null);

  // Role-preview states (what pages/actions does the selected role unlock?)
  const [rolePermissions, setRolePermissions] = useState([]);
  const [rolePermsLoading, setRolePermsLoading] = useState(false);
  const [rolePermsError, setRolePermsError] = useState(null);

  const loadUserRoles = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.USER_ROLES_LIST)) {
      setUserRoles([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ...filters,
      };
      
      const response = await getUserRoles(params);
      if (response && response.data) {
        setData(response.data);
        setUserRoles(response.data.results || []);
      } else {
        setUserRoles([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load user-role assignments:', error);
      setResponseMessage(error.message || t('settingsPages.userRoles.loadFailed'));
      setFailedNotification(true);
      setUserRoles([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  useEffect(() => {
    loadUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  // Fetch the selected role's permissions so the admin can preview exactly
  // which pages/actions the role unlocks before saving. Gated by both the
  // modal being open and by ROLES_VIEW_PERMISSIONS to keep the call tidy.
  useEffect(() => {
    if (!openModal || !formData.role) {
      setRolePermissions([]);
      setRolePermsError(null);
      return undefined;
    }
    if (!hasPermission(PERMISSIONS.ROLES_VIEW_PERMISSIONS)) {
      setRolePermissions([]);
      setRolePermsError(null);
      return undefined;
    }
    let cancelled = false;
    setRolePermsLoading(true);
    setRolePermsError(null);
    getRolePermissions(formData.role)
      .then((res) => {
        if (cancelled) return;
        const data = res && res.data;
        setRolePermissions(getPaginatedOrKeyedResults(data, 'permissions'));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load role permissions preview:', err);
        setRolePermsError(err.message || 'error');
        setRolePermissions([]);
      })
      .finally(() => {
        if (!cancelled) setRolePermsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openModal, formData.role, hasPermission]);

  const handleOpenModal = useCallback((userRole = null) => {
    if (userRole) {
      setEditingUserRole(userRole);
      setFormData({
        user: userRole.user ? { id: userRole.user } : null,
        role: userRole.role || '',
        expires_at: userRole.expires_at ? userRole.expires_at.split('T')[0] : '',
        is_active: userRole.is_active !== undefined ? userRole.is_active : true,
        branch: userRole.branch || null,
      });
    } else {
      setEditingUserRole(null);
      setFormData({
        user: null,
        role: '',
        expires_at: '',
        is_active: true,
        branch: null,
      });
    }
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingUserRole(null);
  };

  const handleSave = async () => {
    if (editingUserRole ? !hasPermission(PERMISSIONS.USER_ROLES_UPDATE) : !hasPermission(PERMISSIONS.USER_ROLES_CREATE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    try {
      setLoading(true);
      const dataToSend = {
        ...formData,
        user: formData.user?.id || formData.user,
        expires_at: formData.expires_at ? `${formData.expires_at}T23:59:59Z` : null,
      };
      
      if (editingUserRole) {
        await updateUserRole(editingUserRole.id, dataToSend);
        setResponseMessage(t('settingsPages.userRoles.updatedSuccess'));
      } else {
        await createUserRole(dataToSend);
        setResponseMessage(t('settingsPages.userRoles.createdSuccess'));
      }
      setSuccessNotification(true);
      handleCloseModal();
      loadUserRoles();
    } catch (error) {
      console.error('Failed to save user-role assignment:', error);
      setResponseMessage(error.message || t('settingsPages.userRoles.saveFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback((id) => {
    setSelectedUserRoleId(id);
    setDeleteAlert(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedUserRoleId) return;
    if (!hasPermission(PERMISSIONS.USER_ROLES_DELETE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    
    try {
      setDeleteLoading(true);
      await deleteUserRole(selectedUserRoleId);
      setResponseMessage(t('settingsPages.userRoles.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteAlert(false);
      setSelectedUserRoleId(null);
      loadUserRoles();
    } catch (error) {
      console.error('Failed to delete user-role assignment:', error);
      setResponseMessage(error.message || t('settingsPages.userRoles.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadUserRoles();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const formatDate = useCallback(
    (dateString) => {
      if (!dateString) return t('actions.na');
      try {
        return new Date(dateString).toLocaleDateString();
      } catch {
        return dateString;
      }
    },
    [t]
  );

  const columns = useMemo(
    () => [
      {
        field: 'user',
        headerName: t('settingsPages.userRoles.colUser'),
        flex: 3,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'flex-start', py: 0.25 }}>
            <Typography fontWeight="md">
              {params.row.user_username || `${t('settingsPages.userRoles.userPrefix')}${params.row.user}`}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'role',
        headerName: t('settingsPages.userRoles.colRole'),
        flex: 2,
        minWidth: 180,
        renderCell: (params) => (
          <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'flex-start', py: 0.25 }}>
            <Chip size="small" variant="soft" color="primary">
              {params.row.role_display_name || `${t('settingsPages.userRoles.colRole')} #${params.row.role}`}
            </Chip>
          </Box>
        ),
      },
      {
        field: 'assigned_by',
        headerName: t('settingsPages.userRoles.colAssignedBy'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => (
          <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'flex-start', py: 0.25 }}>
            {params.row.assigned_by_username ||
              (params.row.assigned_by != null
                ? `${t('settingsPages.userRoles.userPrefix')}${params.row.assigned_by}`
                : t('actions.na'))}
          </Box>
        ),
      },
      {
        field: 'assigned_at',
        headerName: t('settingsPages.userRoles.colAssignedAt'),
        flex: 2,
        minWidth: 160,
        renderCell: (params) => (
          <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'flex-start', py: 0.25 }}>
            {formatDate(params.row.assigned_at)}
          </Box>
        ),
      },
      {
        field: 'expires_at',
        headerName: t('settingsPages.userRoles.colExpiresAt'),
        flex: 2,
        minWidth: 160,
        renderCell: (params) => (
          <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'flex-start', py: 0.25 }}>
            {formatDate(params.row.expires_at)}
          </Box>
        ),
      },
      {
        field: 'is_active',
        headerName: t('settingsPages.permissions.colStatus'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => (
          <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'flex-start', py: 0.25 }}>
            <Chip
              size="small"
              color={params.row.is_active ? 'success' : 'neutral'}
              variant="soft"
            >
              {params.row.is_active
                ? t('settingsPages.permissions.statusActive')
                : t('settingsPages.permissions.statusInactive')}
            </Chip>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: t('settingsPages.userRoles.colActions'),
        flex: 2,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
            {hasPermission(PERMISSIONS.USER_ROLES_UPDATE) && (
              <Tooltip title={t('settingsPages.userRoles.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasPermission(PERMISSIONS.USER_ROLES_DELETE) && (
              <Tooltip title={t('settingsPages.userRoles.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(params.row.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, hasPermission, formatDate, handleOpenModal, handleDelete]
  );

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/settings">
                  {t('settingsPages.userRoles.breadcrumbSettings')}
                </Link>
                <Typography level="body-md">{t('settingsPages.userRoles.breadcrumbCurrent')}</Typography>
              </Breadcrumbs>
              <Typography textAlign={"start"} level="h1">{t('settingsPages.userRoles.pageTitle')}</Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.USER_ROLES_LIST]}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={2.4} lg={2.4}>
                  <Input
                    placeholder={t('settingsPages.userRoles.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4} lg={2.4}>
                  <Input
                    type="number"
                    placeholder={t('settingsPages.userRoles.colUserId')}
                    value={filters.user}
                    onChange={(e) => setFilters(prev => ({ ...prev, user: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4} lg={2.4}>
                  <Input
                    type="number"
                    placeholder={t('settingsPages.userRoles.colRoleId')}
                    value={filters.role}
                    onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4} lg={2.4}>
                  <Select
                    value={filters.is_active === '' ? 'all' : (filters.is_active ? 'active' : 'inactive')}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_active: value === 'all' ? '' : value === 'active' }))}
                  >
                    <Option value="all">{t('settingsPages.permissions.filterAllStatus')}</Option>
                    <Option value="active">{t('settingsPages.permissions.statusActive')}</Option>
                    <Option value="inactive">{t('settingsPages.permissions.statusInactive')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4} lg={2.4}>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />}>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Actions icons */}
              <Grid mt={2} container spacing={2} justifyContent="flex-end">
                <Grid
                  container
                  spacing={1}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start'
                  }}
                >
                  <PermissionGate permissions={[PERMISSIONS.USER_ROLES_CREATE]}>
                    <Grid item>
                      <Tooltip title={t('settingsPages.userRoles.btnAssign')}>
                        <IconButton
                          onClick={() => handleOpenModal()}
                          color="primary"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </PermissionGate>
                </Grid>
              </Grid>

              {/* Table — same layout pattern as ShipmentManagerScreen (full width + horizontal scroll) */}
              <Box
                sx={{
                  width: '100%',
                  minWidth: 0,
                  overflow: 'auto',
                }}
              >
                <DataGrid
                  rows={userRoles || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? data.count : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  initialState={{ pagination: { paginationModel } }}
                  sx={{
                    width: '100%',
                    minWidth: 1000,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Create/Edit Modal */}
            <Modal open={openModal} onClose={handleCloseModal}>
              <ModalDialog
                sx={{
                  maxWidth: 600,
                  width: '100%',
                  maxHeight: '95vh',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <ModalClose />
                <Typography level="h4" sx={{ mb: 2, flexShrink: 0 }}>
                  {editingUserRole ? t('settingsPages.userRoles.modalEditTitle') : t('settingsPages.userRoles.modalAssignTitle')}
                </Typography>
                <Stack
                  spacing={2}
                  sx={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    flex: 1,
                    minHeight: 0,
                    pr: 0.5,
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {!editingUserRole ? (
                    <CustomerOptionList
                      label={t('settingsPages.userRoles.labelUser')}
                      placeholder={t('widgets.searchUsers')}
                      required={true}
                      defaultValue={formData.user}
                      onChange={(event, value) => setFormData(prev => ({ ...prev, user: value }))}
                    />
                  ) : (
                    <Box>
                      <FormLabel sx={{ mb: 0.5 }}>{t('settingsPages.userRoles.labelUser')}</FormLabel>
                      <Typography level="body-md">
                        {editingUserRole.user_username ||
                          (editingUserRole.user != null
                            ? `${t('settingsPages.userRoles.userPrefix')}${editingUserRole.user}`
                            : t('actions.na'))}
                      </Typography>
                    </Box>
                  )}

                  <RoleOptionList
                    required
                    value={formData.role}
                    initialOption={
                      editingUserRole && editingUserRole.role
                        ? {
                            id: editingUserRole.role,
                            display_name: editingUserRole.role_display_name,
                          }
                        : null
                    }
                    onChange={(event, roleId) => setFormData((prev) => ({ ...prev, role: roleId }))}
                  />

                  {formData.role ? (
                    <RolePreviewPanel
                      loading={rolePermsLoading}
                      error={rolePermsError}
                      permissions={rolePermissions}
                      t={t}
                    />
                  ) : null}

                  <FormLabel>{t('settingsPages.userRoles.labelExpires')}</FormLabel>
                  <Input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                      {t('actions.cancel')}
                    </Button>
                    <PermissionGate permissions={editingUserRole ? [PERMISSIONS.USER_ROLES_UPDATE] : [PERMISSIONS.USER_ROLES_CREATE]}>
                      <Button
                        onClick={handleSave}
                        disabled={loading || !formData.user || !formData.role}
                        startDecorator={loading ? <CircularProgress size="sm" /> : <SaveIcon />}
                      >
                        {loading ? t('settingsPages.common.saving') : t('actions.save')}
                      </Button>
                    </PermissionGate>
                  </Box>
                </Stack>
              </ModalDialog>
            </Modal>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <DeleteAlert
              label={t('nav.userRoleAssignments')}
              open={deleteAlert}
              onDelete={confirmDelete}
              onClose={() => {
                setDeleteAlert(false);
                setSelectedUserRoleId(null);
              }}
              loading={deleteLoading}
            />
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

